/**
 * @file DeleteButton.jsx
 * @description A button component for element removal with a confirmation popup.
 * @requires React, PropTypes, semantic-ui-react, react-i18next
 * @exports DeleteButton
 */

import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Popup } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';

import './ActionButtons.css';

/**
 * handleElementAction is a helper function to prevent redirection and perform the action
 * 
 * @function handleEventAction
 * @param {React.SyntheticEvent} eventElement Event element to prevent redirection.
 * @param {Function} action Function to perform the action (delete).
 * @param {string} id Element ID to be passed for the respective action.
 * @param {boolean} [alreadyStarted=false] Flag to indicate if the element has already started.
 * @returns {void}
 * 
 * @example
 * handleElementAction(eventElement, handleDeleteEvent, eventElement.id);
 */
const handleElementAction = async (element, action, id, alreadyStarted=false) => {
    element.stopPropagation(); // Disables the redirection from the div zone
    action(id, alreadyStarted);
};

/**
 * @typedef {Object} ActionButtonProps
 * @property {Function} action - Callback function to handle the delete action. Should accept an `elementId`.
 * @property {string} elementId - Identifier of the element to be deleted.
 * @property {string} [color='brown'] - Color theme of the button.
 * @property {string} [label] - Aria label of the button.
 * @property {string} [icon='trash'] - Icon displayed on the button.
 * @property {string} [confirmationText] - Text displayed in the confirmation popup. Defaults to localized "Popup_TextConfirmDeleteElement" text.
 * @property {string} [buttonConfirmationText] - Text displayed on the confirmation button. Defaults to localized "Pupup_ConfirmDelete" text.
 */

/**
 * DeleteButton is a reusable component specialized for deletion tasks.
 * 
 * @component
 * @param {ActionButtonProps} props - Component properties.
 * @returns {JSX.Element} A styled button component with an action and confirmation popup for deleting elements.
 * 
 * @example
 * const handleDeleteEvent = (id) => console.log(`Deleting element with ID: ${id}`);
 * 
 * // Basic usage
 * <DeleteButton
 *   action={handleDeleteEvent}
 *   elementId={eventElement.id}
 * />
 * 
 * // Customized button
 * <DeleteButton
 *   action={handleDeleteEvent}
 *   elementId={eventElement.id}
 *   color='red'
 *   ariaLabel='Delete this element'
 *   icon='delete'
 *   confirmationText='Are you sure you want to delete this element?'
 *   buttonConfirmationText='Yes, delete it'
 * />
 */
const DeleteButton = ({
    action,
    elementId,
    alreadyStarted,
    color='brown',
    label,
    icon='trash',
    confirmationText,
    buttonConfirmationText
}) => {
    if (!action) {
        throw new Error("DeleteButton: Missing required prop 'action'. Expected a function.");
    }

    if (!elementId) {
        throw new Error("DeleteButton: Missing required prop 'elementId'. Expected a string identifier.");
    }

    const { t } = useTranslation();

    const momoizedHandleElementAction = useCallback((element) => handleElementAction(element, action, elementId, alreadyStarted), [action, elementId]);

    const computedAriaLabel = useMemo(() => label || t('Default_AriaLabelDelete'), [label, t]);
    const computedConfirmationText = useMemo(() => confirmationText || t('Popup_TextConfirmDeleteElement'), [confirmationText, t]);
    const computedButtonConfirmationText = useMemo(() => buttonConfirmationText || t('Pupup_ConfirmDelete'), [buttonConfirmationText, t]);

    return (
        <Popup
            trigger={
                <Button
                    className="delete-element-button"
                    onClick={async (element) => {
                        element.stopPropagation(); // Disables the redirection from the div zone
                    }}
                    icon
                    color={color}
                    aria-label={computedAriaLabel}
                >
                    <Icon name={icon} />
                </Button>
            }
            content={
                <div>
                    <div>{computedConfirmationText}</div>
                    <Button
                        className="delete-element-button"
                        color={color}
                        content={computedButtonConfirmationText}
                        onClick={momoizedHandleElementAction}
                    />
                </div>
            }
            on="click"
            position="top right"
        />
    );
};

DeleteButton.propTypes = {
    action: PropTypes.func.isRequired,
    elementId: PropTypes.string.isRequired,
    color: PropTypes.string,
    label: PropTypes.string,
    icon: PropTypes.string,
    confirmationText: PropTypes.string,
    buttonConfirmationText: PropTypes.string,
};

export default DeleteButton;